/**
 * The tour engine's pure parts (plan
 * guide-can-walk-a-person-through-a-task-highlight, item tour-engine; design:
 * examplebrain note 665e8b13; contract: docs/architecture/guide-anchors.md).
 *
 * A guide arrives from GET /api/me/guides already filtered to what the person
 * may use. Each step names an anchor; the page it lives on and how its
 * completion is observed come from the anchor registry of THIS release
 * (shared/guideAnchorRegistry.ts), unless the step overrides the completion.
 * The engine only ever looks up names it declares: a guide never carries a
 * selector or an address.
 *
 * Progress is derived (deriveProgress): what the console reads, where the
 * person is, what the page shows (a dialog open, a field filled) and which
 * click steps they clicked. Only the running guide, those clicks and the
 * items each new-item read listed when the tour started live in this browser
 * (localStorage, one key per guide), so a tour survives Microsoft's consent
 * round trip and a reload, and nothing is stored on a server.
 */
import { GUIDE_ANCHORS, GUIDE_STATE_READS, isGuideAnchorName } from '../../../../shared/guideAnchorRegistry'
import { guideQueryMatches } from '../../../../shared/guideAnchor'
import { GUIDE_ANCHOR_ATTRIBUTE, GUIDE_ANCHOR_KEY_ATTRIBUTE } from './guideAnchor'

/**
 * One step as the engine runs it: its declared control (label, page), the
 * completion that advances it and, for a state completion, the named read.
 * For a visible/filled completion, `observed` is the anchor whose element is
 * watched (the completion's own anchor, or the step's own when it names none).
 * `known` is false when this release does not declare the anchor (or the
 * state read or observed anchor an override names): the step cannot be shown
 * here. `follows` marks a keyed anchor's step without a key of its own: it
 * acts on the item the tour created (followCreated).
 */
export function resolveStep(step) {
  const declared = isGuideAnchorName(step?.anchor) ? GUIDE_ANCHORS[step.anchor] : null
  const completion = step?.completion || declared?.completion || null
  const read = completion?.kind === 'state' ? GUIDE_STATE_READS[completion.read] || null : null
  const observed = completion?.kind === 'visible' || completion?.kind === 'filled'
    ? completion.anchor || step?.anchor || ''
    : null
  const observedKnown = observed === null || isGuideAnchorName(observed)
  return {
    anchor: step?.anchor || '',
    key: step?.anchorKey ?? null,
    follows: Boolean(declared?.keyed) && (step?.anchorKey ?? null) === null,
    text: step?.text || '',
    label: declared?.label || step?.anchor || '',
    page: declared?.page || null,
    completion,
    read,
    readName: read ? completion.read : null,
    observed,
    known: Boolean(declared) && Boolean(completion) && (completion.kind !== 'state' || Boolean(read)) && observedKnown,
  }
}

/**
 * A new-item read's answer against the ids listed when the tour started:
 * done while another id is listed. `created` is the item the tour made —
 * the one it had already (`previous`) while it is still listed, else the
 * first new one — or null.
 */
export function newItemState(ids, baseline, previous = null) {
  const started = new Set(baseline || [])
  const fresh = (ids || []).map(String).filter((id) => !started.has(id))
  const created = previous !== null && fresh.includes(previous) ? previous : fresh[0] ?? null
  return { done: fresh.length > 0, created }
}

/** No element carries this key: a following step before anything was created matches nothing. */
const NOTHING_CREATED = ''

/**
 * The steps as this page shows them: a following step (`follows`) takes the
 * created item's id as its key, or a key no element carries until there is
 * one, so it never rings or reads another item.
 */
export function followCreated(steps, created) {
  if (!steps.some((step) => step.follows)) return steps
  return steps.map((step) => (step.follows ? { ...step, key: created ?? NOTHING_CREATED } : step))
}

/** Whether a location ({ pathname, search }) is the given page or route ({ path, params }). */
export function locationMatches(target, location) {
  if (!target || !location) return false
  if (location.pathname !== target.path) return false
  return guideQueryMatches(target.params, new URLSearchParams(location.search || ''))
}

/** A plain link to a page: required values as given, "present" as an empty value, "absent" left out. */
export function pageHref(page) {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(page?.params || {})) {
    if (value === null) continue
    query.set(key, value === true ? '' : value)
  }
  const search = query.toString()
  return `${page?.path || '/'}${search ? `?${search}` : ''}`
}

function cssString(value) {
  return `"${String(value).replace(/["\\]/g, '\\$&')}"`
}

/** The selector for a declared anchor (and its key). Only built from registry names. */
export function anchorSelector(name, key = null) {
  const base = `[${GUIDE_ANCHOR_ATTRIBUTE}=${cssString(name)}]`
  return key === null || key === undefined ? base : `${base}[${GUIDE_ANCHOR_KEY_ATTRIBUTE}=${cssString(key)}]`
}

/** The marked element on this page, preferring a visible one; null when the page does not have it. */
export function findAnchorElement(name, key = null, root = globalThis.document) {
  if (!root || !isGuideAnchorName(name)) return null
  const matches = [...root.querySelectorAll(anchorSelector(name, key))]
  return matches.find((element) => element.getClientRects?.().length > 0) || matches[0] || null
}

/** Whether an element counts as on screen: in the page with a laid-out box. */
export function isElementVisible(element) {
  return Boolean(element?.getClientRects?.().length > 0)
}

/**
 * Whether a field counts as filled: a checkbox or radio that is checked, or
 * an input, textarea or select whose value is not empty (surrounding spaces
 * do not count). Any other element is never filled — only fields can be.
 */
export function isFilledElement(element) {
  if (!element || element.nodeType !== 1) return false
  const tag = String(element.tagName || '').toLowerCase()
  if (tag === 'input') {
    const type = String(element.type || 'text').toLowerCase()
    if (type === 'checkbox' || type === 'radio') return element.checked === true
    return String(element.value ?? '').trim() !== ''
  }
  if (tag === 'textarea' || tag === 'select') return String(element.value ?? '').trim() !== ''
  return false
}

/** Whether a declared anchor's element is on screen (a dialog or section open). */
export function isAnchorVisible(name, key = null, root = globalThis.document) {
  const element = findAnchorElement(name, key, root)
  return isElementVisible(element)
}

/** Whether a declared anchor's field has a value. */
export function isAnchorFilled(name, key = null, root = globalThis.document) {
  return isFilledElement(findAnchorElement(name, key, root))
}

/**
 * Whether a guide is for this page: one of its routes is the current path,
 * and when a route names a query, the current address carries it.
 */
export function guideAppliesHere(guide, location) {
  return (guide?.appliesTo?.routes || []).some((route) => {
    const at = route.indexOf('?')
    const path = at === -1 ? route : route.slice(0, at)
    if (path !== location?.pathname) return false
    if (at === -1) return true
    const wanted = new URLSearchParams(route.slice(at + 1))
    const here = new URLSearchParams(location.search || '')
    return [...wanted].every(([key, value]) => here.get(key) === value)
  })
}

/** Identifies a guide's steps, so stored progress is never replayed onto a different tour. */
export function stepsSignature(guide) {
  return (guide?.steps || []).map((step) => `${step.anchor}#${step.anchorKey ?? ''}`).join('|')
}

// ---- Progress is derived, never recorded ------------------------------------

/**
 * Why a step cannot be shown: 'unknown' when this release does not declare its
 * control, else the reason the engine recorded when the page did not have it.
 */
export function skipReason(resolved, recorded) {
  if (!resolved?.known) return 'unknown'
  return recorded || null
}

/**
 * Which steps are done right now, and where the tour is. Nothing here is a
 * record of the past except the person's own clicks:
 *
 *   - a `state` step is done while the feature's read says so (`stateDone`,
 *     re-read for as long as the tour runs), so removing what it made
 *     un-ticks it;
 *   - a `click` step is done once the person clicked its control (`clicked`,
 *     the only completion the browser keeps) — while the observed steps
 *     before it still read done, or a later observed or state step reads
 *     done. Closing the dialog un-counts its clicks (cancel); a
 *     later step done keeps them (the dialog saved). Both checks read
 *     the raw rounds, never derived dones, so clicks cannot pin each
 *     other into done;
 *   - a `route` step is done while the address is its route, or when a later
 *     `state` or `click` step is done (the person is past it: a step on
 *     another page is done). A later step done only by the address does not
 *     count, so a closing route (setup closed) never ticks the steps before it;
 *   - a `visible` step is done while its observed anchor is on screen
 *     (`domDone`), and a `filled` step while its observed field has a value —
 *     both read again from the page for as long as the tour runs, so closing
 *     the dialog un-ticks them. Either is also done when a later non-route
 *     step is done (saving the dialog ticks the steps inside it).
 *
 * The current step is the first step not done. A step whose control could not
 * be shown is passed, except a `state` step: the next steps build on it, so
 * the tour stops there (`stopped`) until it is done or shown.
 */
export function deriveProgress(resolvedSteps, { stateDone = {}, domDone = {}, clicked = [], skipped = {}, location } = {}) {
  const total = resolvedSteps.length
  const done = new Array(total).fill(false)
  const reasons = resolvedSteps.map((resolved, index) => skipReason(resolved, skipped[index]))
  // A click counts while every observed (visible/filled) step before it still
  // reads done (its dialog is still open on what the person did), or a
  // later observed or state step reads done (the flow completed past it).
  // Vacuous without dialogs, so tours without observed steps behave
  // exactly as before.
  const clickCounts = (index) => {
    for (let before = 0; before < index; before += 1) {
      const kind = resolvedSteps[before]?.known ? resolvedSteps[before].completion.kind : null
      if ((kind !== 'visible' && kind !== 'filled') || domDone[before] === true) continue
      // Undone observed step: excused only by a raw pin after it (the flow
      // completed past it).
      let pinned = false
      for (let later = before + 1; later < total; later += 1) {
        const laterKind = resolvedSteps[later]?.known ? resolvedSteps[later].completion.kind : null
        if (laterKind === 'state' && stateDone[later] === true) { pinned = true; break }
        if ((laterKind === 'visible' || laterKind === 'filled') && domDone[later] === true) { pinned = true; break }
      }
      if (!pinned) return false
    }
    return true
  }
  let laterDone = false
  for (let index = total - 1; index >= 0; index -= 1) {
    const resolved = resolvedSteps[index]
    const kind = resolved?.known ? resolved.completion.kind : null
    if (kind === 'state') done[index] = stateDone[index] === true
    else if (kind === 'click') done[index] = clicked.includes(index) && clickCounts(index)
    else if (kind === 'route') done[index] = locationMatches(resolved.completion, location) || laterDone
    else if (kind === 'visible' || kind === 'filled') done[index] = domDone[index] === true || laterDone
    if (done[index] && kind !== 'route') laterDone = true
  }
  for (let index = 0; index < total; index += 1) {
    if (done[index]) continue
    const reason = reasons[index]
    const halts = resolvedSteps[index]?.completion?.kind === 'state'
    if (reason && !halts) continue
    return { done, reasons, current: index, finished: false, stopped: reason ? { index, reason } : null }
  }
  return { done, reasons, current: null, finished: true, stopped: null }
}

// ---- What this browser keeps -------------------------------------------------
//
// Only which guide runs (name, set version, step signature), which click
// steps the person clicked and, per new-item read, the ids it listed when the
// tour started: everything else is read again while the tour runs.

const PROGRESS_PREFIX = 'ops.guide.tour:'
const ACTIVE_KEY = 'ops.guide.tour.active'

function storage() {
  try { return globalThis.localStorage || null } catch { return null }
}

/** A fresh tour of a guide. */
export function newProgress(guide, version) {
  return { name: guide.name, version: version || null, signature: stepsSignature(guide), clicked: [], baseline: {} }
}

const isBaseline = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value)
  && Object.values(value).every((ids) => Array.isArray(ids) && ids.every((id) => typeof id === 'string'))

/** The ids each new-item read listed when the tour started ({} when none is kept). */
export function storedBaseline(progress) {
  return isBaseline(progress?.baseline) ? progress.baseline : {}
}

/** Keep the tour (and which tour is running) in localStorage: only what cannot be read again. */
export function saveProgress(progress) {
  const store = storage()
  if (!store || !progress?.name) return
  const { name, version, signature, clicked } = progress
  try {
    store.setItem(`${PROGRESS_PREFIX}${name}`, JSON.stringify({ name, version: version ?? null, signature, clicked: clicked || [], baseline: storedBaseline(progress) }))
    store.setItem(ACTIVE_KEY, name)
  } catch { /* without storage the tour still runs; it only cannot resume */ }
}

const isStoredTour = (progress, name) => Boolean(progress) && progress.name === name && typeof progress.signature === 'string'
  && Array.isArray(progress.clicked) && progress.clicked.every(Number.isInteger)

/** The running tour's stored progress, or null. */
export function loadActiveProgress() {
  const store = storage()
  if (!store) return null
  try {
    const name = store.getItem(ACTIVE_KEY)
    if (!name) return null
    const progress = JSON.parse(store.getItem(`${PROGRESS_PREFIX}${name}`) || 'null')
    return isStoredTour(progress, name) ? progress : null
  } catch {
    return null
  }
}

/** Forget a guide's progress (and the running tour, when it is that one). */
export function clearProgress(name) {
  const store = storage()
  if (!store) return
  try {
    store.removeItem(`${PROGRESS_PREFIX}${name}`)
    if (store.getItem(ACTIVE_KEY) === name) store.removeItem(ACTIVE_KEY)
  } catch { /* nothing kept */ }
}

/**
 * Whether stored progress still fits the guide as served now: the same set
 * version, or the same steps (a publish elsewhere changes the set version
 * without touching this tour).
 */
export function progressFits(progress, guide, version) {
  if (!progress || !guide || progress.name !== guide.name) return false
  if (!Array.isArray(progress.clicked) || progress.clicked.some((index) => index < 0 || index >= guide.steps.length)) return false
  return progress.version === version || progress.signature === stepsSignature(guide)
}
